import type { WithId } from '@medplum/core';
import type { PlanDefinition } from '@medplum/fhirtypes';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { SettingsPageFrame } from '@eluve/blocks';
import { IconTile, PlanShapeBadge } from '@eluve/care-plan-render';
import {
  Badge,
  Icon,
  Input,
  LinkButton,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Text,
} from '@eluve/components';
import { useTranslation } from '@eluve/i18n';
import { useMedplum } from '@eluve/medplum-client';
import { PLAN_SHAPES, planShapeOf } from '@eluve/plan-shapes';

import {
  fetchPlanDefinitions,
  planConditions,
  planDefinitionTitle,
} from './care-plans';

// Tokenized substring match over a protocol's searchable fields — cheap to run
// on every keystroke for the dozen-or-so protocols we expect. Shape filtering
// is deliberately client-side: no custom SearchParameter.
function filterPlans(
  plans: WithId<PlanDefinition>[],
  query: string,
): WithId<PlanDefinition>[] {
  const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) {
    return plans;
  }
  return plans.filter((pd) => {
    const shape = planShapeOf(pd);
    const hay = [
      planDefinitionTitle(pd),
      planConditions(pd).join(' '),
      shape ? PLAN_SHAPES[shape].label : '',
      pd.status ?? '',
      pd.version ?? '',
    ]
      .join(' ')
      .toLowerCase();
    return terms.every((t) => hay.includes(t));
  });
}

const CarePlansListContent: React.FC = () => {
  const { t } = useTranslation();
  const { client, enabled } = useMedplum();
  const [plans, setPlans] = useState<WithId<PlanDefinition>[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');

  useEffect(() => {
    if (!client) {
      return;
    }
    let cancelled = false;
    fetchPlanDefinitions(client)
      .then((result) => {
        if (!cancelled) {
          setPlans(result);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [client]);

  const filtered = useMemo(
    () => filterPlans(plans ?? [], searchInput),
    [plans, searchInput],
  );

  if (!enabled && plans === null) {
    return (
      <Text color="secondary">{t('carePlans.templates.requiresMedplum')}</Text>
    );
  }

  return (
    <div>
      <Text color="secondary">
        {plans
          ? t(
              plans.length === 1
                ? 'carePlans.templates.countOne'
                : 'carePlans.templates.countMany',
              { count: plans.length.toLocaleString() },
            )
          : t('carePlans.templates.loading')}
      </Text>

      <div className="mt-4 max-w-sm">
        <Input
          placeholder={t('carePlans.templates.searchPlaceholder')}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
      </div>

      {error ? (
        <Text className="py-10 text-text-negative-strong">
          {t('carePlans.templates.loadFailed', { error })}
        </Text>
      ) : plans === null ? (
        <div className="mt-6 space-y-2">
          <Skeleton className="h-12 w-full rounded-lg" />
          <Skeleton className="h-12 w-full rounded-lg opacity-60" />
          <Skeleton className="h-12 w-full rounded-lg opacity-30" />
        </div>
      ) : (
        <div className="mt-4">
          <PlanTable plans={filtered} />
          {filtered.length === 0 && (
            <Text
              color="secondary"
              className="grid place-items-center px-6 py-16"
            >
              {searchInput.trim() ? (
                <span>
                  {t('carePlans.templates.noMatchesPrefix')}
                  <Text inline color="primary">
                    {searchInput}
                  </Text>
                  {t('carePlans.templates.noMatchesSuffix')}
                </span>
              ) : (
                t('carePlans.templates.emptyState')
              )}
            </Text>
          )}
        </div>
      )}
    </div>
  );
};

export const CarePlansListPage: React.FC = () => {
  const { t } = useTranslation();
  return (
    <SettingsPageFrame
      size="wide"
      title={t('carePlans.templates.pageTitle')}
      actions={
        <LinkButton to="new" size="sm">
          <Icon name="Sparkles" size="auto" />
          {t('carePlans.templates.newPlan')}
        </LinkButton>
      }
    >
      <CarePlansListContent />
    </SettingsPageFrame>
  );
};

function PlanTable({ plans }: { plans: WithId<PlanDefinition>[] }) {
  const { t } = useTranslation();
  if (plans.length === 0) {
    return null;
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t('carePlans.templates.colTemplate')}</TableHead>
          <TableHead>{t('carePlans.templates.colConditions')}</TableHead>
          <TableHead>{t('carePlans.templates.colShape')}</TableHead>
          <TableHead>{t('carePlans.templates.colVersion')}</TableHead>
          <TableHead>{t('carePlans.templates.colStatus')}</TableHead>
          <TableHead className="w-12" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {plans.map((pd) => (
          <PlanRow key={pd.id} plan={pd} />
        ))}
      </TableBody>
    </Table>
  );
}

function PlanRow({ plan }: { plan: WithId<PlanDefinition> }) {
  const title = planDefinitionTitle(plan);
  const conditions = planConditions(plan);
  const shape = planShapeOf(plan);

  return (
    <TableRow className="group">
      <TableCell>
        <Link to={plan.id} className="flex items-center gap-3">
          <IconTile className="bg-muted text-foreground/70">
            <Icon name="ClipboardList" size="auto" />
          </IconTile>
          <Text inline className="min-w-0 truncate">
            {title}
          </Text>
        </Link>
      </TableCell>
      <TableCell>
        <Text inline color="secondary">
          {conditions.length > 0 ? conditions.join(', ') : '—'}
        </Text>
      </TableCell>
      <TableCell>
        <PlanShapeBadge shape={shape} />
      </TableCell>
      <TableCell>
        <Text inline size="xs" color="secondary" className="font-mono">
          {plan.version ?? '—'}
        </Text>
      </TableCell>
      <TableCell>
        <PlanStatus status={plan.status} />
      </TableCell>
      <TableCell className="text-right">
        <Icon
          name="ChevronRight"
          size="auto"
          className="inline size-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100"
        />
      </TableCell>
    </TableRow>
  );
}

function PlanStatus({ status }: { status: PlanDefinition['status'] }) {
  if (!status) {
    return (
      <Text inline color="secondary">
        —
      </Text>
    );
  }
  return (
    <Badge variant={status === 'active' ? 'success' : 'secondary'}>
      {status}
    </Badge>
  );
}
